import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, Phone, MapPin, Send, MessageSquare, Clock, Globe, ChevronDown, ChevronUp } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
const ContactUsPage = () => {
    const location = useLocation();
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
    });

    const [submitted, setSubmitted] = useState(false);
    const [activeFaq, setActiveFaq] = useState(null);

    useEffect(() => {
        window.scrollTo(0, 0);
        const params = new URLSearchParams(location.search);
        const subject = params.get('subject');
        if (subject) {
            setFormData(prev => ({ ...prev, subject }));
        }
    }, [location.search]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        console.log('Form submitted:', formData);
        setSubmitted(true);
        setFormData({
            name: '',
            email: '',
            phone: '',
            subject: '',
            message: '',
        });
        setTimeout(() => setSubmitted(false), 5000);
    };

    const contactDetails = [
        {
            icon: MapPin,
            label: 'Our Office',
            value: 'Ramasamy Plaza, First Floor No: 122/3, Plot No:1, KG Nanmai Nagar, Kovalan Nagar, Madurai - 625003',
            color: 'bg-blue-50 text-blue-600',
        },
        {
            icon: Phone,
            label: 'Call Us',
            value: '+91 8807683685',
            color: 'bg-green-50 text-green-600',
        },
        {
            icon: Mail,
            label: 'Email Us',
            value: 'hr@miniapptech.in',
            color: 'bg-primary/10 text-primary',
        },
        {
            icon: Clock,
            label: 'Working Hours',
            value: 'Mon - Fri: 9:30 AM - 6:30 PM',
            color: 'bg-purple-50 text-purple-600',
        },
    ];

    const faqs = [
        {
            q: "What services does Mini App Tech provide?",
            a: "We specialize in custom web development, mobile app creation, enterprise software solutions, and digital transformation consulting."
        },
        {
            q: "How long does a typical project take?",
            a: "Timeline varies by complexity. A simple website might take 4-6 weeks, while complex enterprise applications can take 3-6 months."
        },
        {
            q: "Do you offer post-launch support?",
            a: "Yes, we provide various maintenance and support packages to ensure your software remains secure and up-to-date."
        },
        {
            q: "Can you help with existing legacy systems?",
            a: "Absolutely. We have extensive experience in modernizing legacy applications and migrating them to modern tech stacks."
        }
    ];

    return (
        <div className="pt-24 min-h-screen bg-white">
             <Helmet>
                <title>Contact Us | Mini App Tech</title>
                <meta name="description" content="Get in touch with Mini App Tech for your next digital project. We're here to help you navigate your digital journey." />
            </Helmet>
            {/* Hero Section */}
            <section className="relative py-20 bg-dark text-white overflow-hidden">
                <div className="absolute inset-0 opacity-20">
                    <div className="absolute top-0 -right-1/4 w-1/2 h-full bg-primary/30 blur-[120px] rounded-full translate-y-[-20%]" />
                    <div className="absolute bottom-0 -left-1/4 w-1/2 h-full bg-secondary/30 blur-[120px] rounded-full translate-y-[20%]" />
                </div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
                    <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-5xl md:text-7xl font-bold mb-6"
                    >
                        Let's Start a <span className="text-primary">Conversation</span>
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="text-xl text-gray-400 max-w-2xl mx-auto"
                    >
                        Have a question or a project in mind? We're here to help you navigate your digital journey.
                    </motion.p>
                </div>
            </section>

            {/* Main Contact Section */}
            <section className="py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-16">
                    {/* Info Side */}
                    <div className="lg:col-span-2 space-y-12">
                        <div>
                            <h2 className="text-3xl font-bold text-dark mb-6">Contact Information</h2>
                            <p className="text-gray-600 mb-8">
                                Reach out to us through any of these channels our team will get back to you within 24 hours.
                            </p>
                        </div>

                        <div className="space-y-6">
                            {contactDetails.map((item, idx) => (
                                <motion.div
                                    key={idx}
                                    initial={{ opacity: 0, x: -20 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    transition={{ delay: idx * 0.1 }}
                                    className="flex items-start gap-4 p-4 rounded-2xl border border-gray-50 hover:border-primary/20 transition-all hover:shadow-soft group"
                                >
                                    <div className={`p-3 rounded-xl ${item.color} group-hover:scale-110 transition-transform`}>
                                        <item.icon size={24} />
                                    </div>
                                    <div>
                                        <p className="text-sm font-bold text-gray-400 uppercase tracking-wider">{item.label}</p>
                                        <p className="text-lg font-bold text-dark">{item.value}</p>
                                    </div>
                                </motion.div>
                            ))}
                        </div>

                        {/* Social Connection */}
                        <div className="pt-8 border-t border-gray-100">
                            <h3 className="font-bold text-dark mb-4">Connect on Social</h3>
                            <div className="flex gap-4">
                                {[
                                    { name: 'LinkedIn', url: 'https://in.linkedin.com/company/miniapptech' },
                                    { name: 'Instagram', url: 'https://www.instagram.com/miniapptech' },
                                    { name: 'Instagram', url: 'https://www.instagram.com/miniapptech' }
                                ].map((s) => (
                                    <a
                                        key={s.name}
                                        href={s.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="px-4 py-2 bg-light rounded-lg text-sm font-bold text-gray-600 hover:bg-primary hover:text-white transition-all"
                                    >
                                        {s.name}
                                    </a>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Form Side */}
                    <div className="lg:col-span-3">
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            className="bg-white p-8 md:p-12 rounded-3xl border border-gray-100 shadow-xl"
                        >
                            <div className="flex items-center gap-3 text-primary font-bold mb-8">
                                <MessageSquare size={24} /> SEND US A MESSAGE
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="space-y-2">
                                        <label className="text-sm font-bold text-gray-700">Full Name</label>
                                        <input
                                            type="text" name="name" value={formData.name} onChange={handleChange} required
                                            className="w-full px-5 py-3 rounded-xl border border-gray-200 focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all bg-light/50"
                                            placeholder="John Doe"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-sm font-bold text-gray-700">Email Address</label>
                                        <input
                                            type="email" name="email" value={formData.email} onChange={handleChange} required
                                            className="w-full px-5 py-3 rounded-xl border border-gray-200 focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all bg-light/50"
                                            placeholder="john@example.com"
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="space-y-2">
                                        <label className="text-sm font-bold text-gray-700">Phone Number</label>
                                        <input
                                            type="tel" name="phone" value={formData.phone} onChange={handleChange}
                                            className="w-full px-5 py-3 rounded-xl border border-gray-200 focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all bg-light/50"
                                            placeholder="+91 00000 00000"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-sm font-bold text-gray-700">Subject</label>
                                        <input
                                            type="text" name="subject" value={formData.subject} onChange={handleChange} required
                                            className="w-full px-5 py-3 rounded-xl border border-gray-200 focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all bg-light/50"
                                            placeholder="Web Development Inquiry"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-sm font-bold text-gray-700">Message</label>
                                    <textarea
                                        name="message" value={formData.message} onChange={handleChange} required rows="5"
                                        className="w-full px-5 py-3 rounded-xl border border-gray-200 focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all bg-light/50 resize-none"
                                        placeholder="Tell us about your project or inquiry..."
                                    />
                                </div>

                                <AnimatePresence>
                                    {submitted && (
                                        <motion.div
                                            initial={{ opacity: 0, height: 0 }}
                                            animate={{ opacity: 1, height: 'auto' }}
                                            exit={{ opacity: 0, height: 0 }}
                                            className="bg-green-50 border border-green-200 text-green-700 p-4 rounded-xl flex items-center gap-2"
                                        >
                                            <Globe size={20} /> Success! Your message has been sent.
                                        </motion.div>
                                    )}
                                </AnimatePresence>

                                <button
                                    type="submit"
                                    className="w-full py-4 bg-primary hover:bg-secondary text-white rounded-xl font-bold text-lg shadow-lg hover:shadow-primary/30 transition-all flex items-center justify-center gap-3"
                                >
                                    Send Message <Send size={20} />
                                </button>
                            </form>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Career Openings Section */}
            <section className="py-24 bg-white border-t border-gray-100">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
                        <div>
                            <h2 className="text-4xl font-bold text-dark mb-4">Current <span className="text-primary">Openings</span></h2>
                            <p className="text-gray-600 max-w-xl">
                                Join our growing team in Madurai. We're looking for driven individuals
                                to help us scale our digital impact.
                            </p>
                        </div>
                        <div className="hidden md:block">
                            <span className="text-sm font-bold text-gray-400 uppercase tracking-widest">3 Positions Available</span>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {[
                            { title: 'BDE (Business Development Executive)', type: 'Full-time', location: 'Madurai', dept: 'Sales' },
                            { title: 'Sales Representative', type: 'Full-time', location: 'Madurai', dept: 'Sales' },
                            { title: 'Admin Executive', type: 'Full-time', location: 'Madurai', dept: 'Operations' }
                        ].map((job, idx) => (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ delay: idx * 0.1 }}
                                whileHover={{ y: -5 }}
                                className="p-8 rounded-3xl border border-gray-100 bg-white shadow-soft hover:shadow-soft-lg hover:border-primary/20 transition-all flex flex-col md:flex-row md:items-center justify-between gap-6"
                            >
                                <div>
                                    <div className="flex items-center gap-2 mb-2">
                                        <span className="px-3 py-1 bg-primary/10 text-primary text-[10px] font-bold uppercase tracking-wider rounded-full">
                                            {job.dept}
                                        </span>
                                        <span className="text-gray-400 text-xs font-medium">{job.type}</span>
                                    </div>
                                    <h3 className="text-xl font-bold text-dark mb-1">{job.title}</h3>
                                    <p className="text-sm text-gray-500 flex items-center gap-1">
                                        <Globe size={14} /> {job.location}
                                    </p>
                                </div>
                                <button
                                    onClick={() => {
                                        const form = document.querySelector('form');
                                        form?.scrollIntoView({ behavior: 'smooth' });
                                        setFormData(prev => ({ ...prev, subject: `Application for ${job.title}` }));
                                    }}
                                    className="px-6 py-2 bg-dark text-white text-sm font-bold rounded-xl hover:bg-primary transition-colors whitespace-nowrap"
                                >
                                    Apply Now
                                </button>
                            </motion.div>
                        ))}
                    </div>

                    <div className="mt-12 p-8 rounded-3xl bg-light/50 border border-dashed border-gray-200 text-center">
                        <p className="text-gray-600">
                            Don't see a role that fits? Send your CV to <a href="mailto:hr@miniapptech.in" className="text-primary font-bold hover:underline">hr@miniapptech.in</a> and we'll keep you in mind for future opportunities.
                        </p>
                    </div>
                </div>
            </section>

            {/* FAQ Section */}
            <section className="py-24 bg-light">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl font-bold text-dark mb-4">Common <span className="text-primary">Questions</span></h2>
                        <p className="text-gray-600">Quick answers to frequently asked questions about our process and services.</p>

                        <div className="space-y-4">
                            {faqs.map((faq, idx) => (
                                <div key={idx} className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-soft">
                                    <button
                                        onClick={() => setActiveFaq(activeFaq === idx ? null : idx)}
                                        className="w-full p-6 text-left flex items-center justify-between font-bold text-dark hover:text-primary transition-colors"
                                    >
                                        {faq.q}
                                        {activeFaq === idx ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                                    </button>
                                    <AnimatePresence>
                                        {activeFaq === idx && (
                                            <motion.div
                                                initial={{ height: 0, opacity: 0 }}
                                                animate={{ height: 'auto', opacity: 1 }}
                                                exit={{ height: 0, opacity: 0 }}
                                                className="px-6 pb-6 text-gray-600 leading-relaxed border-t border-gray-50 pt-4"
                                            >
                                                {faq.a}
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Map Section */}
            <section className="py-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="h-[450px] bg-gray-100 rounded-[2rem] overflow-hidden shadow-2xl border border-gray-100 relative group"
                    >
                        <iframe
                            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3930.464763880803!2d78.0987570745074!3d9.895193874830428!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x834bc88d5395a74b%3A0x5fdf094635fa8c4!2sMini%20App%20Tech!5e0!3m2!1sen!2sin!4v1771821529485!5m2!1sen!2sin"
                            width="100%"
                            height="100%"
                            style={{ border: 0 }}
                            allowFullScreen=""
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                            title="Mini App Tech Location"
                        ></iframe>
                    </motion.div>
                </div>
            </section>
        </div>
    );
};

export default ContactUsPage;
